import { useState, useEffect, useCallback, useRef } from 'react';

// 🔹 helper debounce biar search gak fetch setiap ketik
function useDebounce(value, delay = 500) {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);

    return debounced;
}

export default function usePaginatedTable({
    apiFn,
    params = {},
    initialPage = 1,
    initialPerPage = 10,
    initialFilter = {},
    autoFetch = true,
}) {
    // 🔹 States utama
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: initialPage,
        perPage: initialPerPage,
        total: 0,
    });
    const [filters, setFilters] = useState(initialFilter);
    const [sort, setSort] = useState({ orderBy: null, orderDirection: 'asc' });

    // 🔹 Debounce agar search input tidak refetch tiap ketik
    const debouncedFilters = useDebounce(filters, 400);

    // 🔹 Ref untuk prevent double fetch
    const isFetchingRef = useRef(false);

    // 🔹 Fetch Data (utama)
    const fetchData = useCallback(async () => {
        if (isFetchingRef.current) {
            // console.log('Fetch already in progress, skipping...');
            return;
        }

        isFetchingRef.current = true;

        setLoading(true);
        setError(null);
        try {
            const queryParams = {
                page: pagination.page,
                per_page: pagination.perPage,
                ...params,
                ...debouncedFilters,
                ...(sort.orderBy
                    ? { sort_by: sort.orderBy, sort: sort.orderDirection }
                    : {}),
            };
            // console.log('Fetching with params:', queryParams);
            const response = await apiFn(queryParams);

            // Tangani berbagai struktur API respons
            const meta = response.data || {};
            const resData = response.data.data || response.data;
            const items = resData.data || resData;
            // console.log('Fetched data:', response);
            setData(items);
            setPagination((prev) => ({
                ...prev,
                total: resData.total || 0,
                page: resData.current_page || prev.page,
            }));
        } catch (err) {
            console.error('Pagination fetch failed:', err);
            setError(err.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [apiFn, params, pagination.page, pagination.perPage, JSON.stringify(params), JSON.stringify(debouncedFilters), sort.orderBy, sort.orderDirection]);

    // 🔹 Auto-fetch setiap ada perubahan filter, page, sort
    useEffect(() => {
        if (autoFetch) {
            fetchData();
        }
    }, [
        pagination.page,
        pagination.perPage,
        JSON.stringify(debouncedFilters), // trigger hanya saat debouncedFilters berubah
        sort.orderBy,
        sort.orderDirection
    ]);

    // 🔹 Pagination Handlers
    const setPage = (newPage) => setPagination((prev) => ({ ...prev, page: newPage }));
    const setPerPage = (newPerPage) =>
        setPagination((prev) => ({ ...prev, perPage: newPerPage, page: 1 }));

    // 🔹 Sorting Handler
    const handleSort = (columnId) => {
        setSort((prev) => ({
            orderBy: columnId,
            orderDirection:
                prev.orderBy === columnId && prev.orderDirection === 'asc' ? 'desc' : 'asc',
        }));
    };

    // 🔹 Filter helpers (optional convenience)
    const setFilterValue = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const resetFilters = () => setFilters(initialFilter);

    return {
        data,
        loading,
        error,
        pagination,
        setPage,
        setPerPage,
        filters,
        setFilters,
        setFilterValue,
        resetFilters,
        sort,
        handleSort,
        fetchData,
    };
}
